/**
 * Crescent Flexipack — Home.
 * Matches the client's uploaded homepage mockup section-by-section.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowRight, Users, Globe, Settings, Shield, Leaf, Handshake,
  Package, Layers, Scissors, Wind, CheckCircle2, MessageCircle,
} from "lucide-react";
import CrescentLayout from "./CrescentLayout";
import usePageSeo, { useFaqSchema, useBreadcrumbSchema } from "@/hooks/usePageSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { users: Users, globe: Globe, settings: Settings, shield: Shield, leaf: Leaf, handshake: Handshake, printer: Settings, layers: Layers, wind: Wind, scissors: Scissors, package: Package, "shield-check": Shield };

export default function CrescentHome() {
  const [site, setSite] = useState(null);

  usePageSeo({
    title: "Crescent Flexi Pack — Flexible Packaging Manufacturer in Kerala, India",
    description: "Custom flexible packaging, printed pouches and laminated rolls — manufactured in Kerala, India for food, seafood, pet-food, tea, coffee, spices and private-label buyers worldwide.",
  });
  useBreadcrumbSchema([{ name: "Home", url: "/crescent" }]);
  useFaqSchema([
    { question: "Where is Crescent Flexi Pack located?", answer: "Our manufacturing facility is in Alappuzha, Kerala, India. We serve Indian and international customers across food, seafood, pet-food and private-label markets." },
    { question: "What products do you manufacture?", answer: "Custom printed pouches (stand-up, zipper, center-seal, side-seal, gusseted), printed laminated rolls, LDPE/LLDPE films, recyclable poly bags and multi-layer laminated flexible packaging." },
    { question: "Do you serve international markets?", answer: "Yes. Many of our customers are exporters shipping food, seafood and consumer products to international markets. We also work directly with international brands." },
  ]);

  useEffect(() => {
    axios.get(`${API}/crescent/site`).then((r) => setSite(r.data)).catch(() => {});
  }, []);

  const products = site?.products || [];
  const industries = site?.industries || [];
  const capabilities = site?.capabilities || [];

  return (
    <CrescentLayout>
      <div data-testid="crescent-home">
        {/* Hero */}
        <section className="relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 text-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-100" data-testid="hero-overline">PRINTING · PACKAGING · A SUSTAINABLE TOMORROW</p>
                <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4" data-testid="hero-headline">
                  Flexible Packaging<br />
                  for a <span className="text-amber-200">Better Tomorrow</span>
                </h1>
                <p className="mt-5 text-lg text-white/90 max-w-xl leading-relaxed">
                  High-quality, innovative and sustainable flexible packaging solutions for food, seafood,
                  agriculture, personal care and consumer products — manufactured in Kerala, India for
                  Indian and international brands.
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link to="/crescent/products" data-testid="hero-explore" className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-6 py-3 rounded-full inline-flex items-center gap-2 transition-colors">
                    Explore Our Products <ArrowRight size={16} />
                  </Link>
                  <Link to="/crescent/request-quote" data-testid="hero-quote" className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold px-6 py-3 rounded-full inline-flex items-center gap-2 transition-colors">
                    Request a Quote
                  </Link>
                </div>
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
                  {["High Quality Printing", "Durable & Safe Packaging", "Custom Solutions", "Sustainable Materials"].map((p) => (
                    <div key={p} className="flex items-start gap-1.5 text-xs text-white/90">
                      <CheckCircle2 size={14} className="text-amber-200 mt-0.5 shrink-0" />
                      {p}
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="relative rounded-2xl shadow-2xl overflow-hidden bg-gradient-to-br from-emerald-800 to-slate-900 aspect-[4/3] p-4" data-testid="hero-image">
                  <div className="grid grid-cols-3 grid-rows-3 gap-3 h-full">
                    {[
                      { label: "Seafood",   bg: "from-blue-500/30 to-blue-700/40" },
                      { label: "Coffee",    bg: "from-amber-800/40 to-stone-800/50" },
                      { label: "Tea",       bg: "from-emerald-500/30 to-emerald-700/40" },
                      { label: "Snacks",    bg: "from-orange-500/30 to-orange-700/40" },
                      { label: "Rice",      bg: "from-yellow-500/30 to-yellow-700/40" },
                      { label: "Spices",    bg: "from-red-500/30 to-red-700/40" },
                      { label: "Pet food",  bg: "from-amber-600/30 to-amber-800/40" },
                      { label: "Frozen",    bg: "from-cyan-500/30 to-cyan-700/40" },
                      { label: "Private label", bg: "from-violet-500/30 to-violet-700/40" },
                    ].map((t) => (
                      <div key={t.label} className={`rounded-xl bg-gradient-to-br ${t.bg} border border-white/10 flex items-end p-3`}>
                        <span className="text-xs font-bold text-white leading-tight">{t.label}</span>
                      </div>
                    ))}
                  </div>
                  <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-slate-950/60 via-transparent to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Industries We Serve */}
        <section className="py-14 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
              <h2 className="font-heading font-extrabold text-slate-900 text-3xl sm:text-4xl">Industries We Serve</h2>
              <p className="text-sm text-slate-500">Reliable Packaging Solutions for Diverse Industries</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="industries-grid">
              {industries.length > 0 ? industries.map((i) => (
                <Link key={i.slug} to={`/crescent/industries/${i.slug}`} data-testid={`industry-card-${i.slug}`}
                  className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-emerald-500 hover:shadow-lg transition-all">
                  <div className={`h-28 bg-slate-100 bg-cover bg-center bg-gradient-to-br ${i.fallback_gradient || 'from-blue-700 to-slate-900'}`} style={{ backgroundImage: `url(${i.image})` }} />
                  <div className="p-3">
                    <div className="font-bold text-slate-900 text-sm leading-tight group-hover:text-emerald-700">{i.title.replace(/ Packaging$/, "")}</div>
                    <ArrowRight size={14} className="mt-2 text-slate-400 group-hover:text-emerald-600" />
                  </div>
                </Link>
              )) : null}
            </div>
            <div className="mt-6 text-right">
              <Link to="/crescent/industries" className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-800">
                View all industries <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-14 lg:py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end gap-6 mb-8">
              <h2 className="font-heading font-extrabold text-slate-900 text-3xl sm:text-4xl">Why<br />Choose Us</h2>
              <div className="h-1 w-16 bg-emerald-500 rounded-full mb-2" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4" data-testid="why-grid">
              {(site?.why || []).map((w, i) => {
                const Icon = ICONS[w.icon] || Users;
                return (
                  <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 text-center" data-testid={`why-card-${i}`}>
                    <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center mx-auto">
                      <Icon size={18} />
                    </div>
                    <div className="font-bold text-slate-900 mt-3 text-sm">{w.stat}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{w.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Our Packaging Solutions */}
        <section className="py-14 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
              <h2 className="font-heading font-extrabold text-slate-900 text-3xl sm:text-4xl">Our Packaging Solutions</h2>
              <p className="text-sm text-slate-500">Customizable formats, materials and designs for your unique needs</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="products-grid">
              {products.length > 0 ? products.map((p) => (
                <Link key={p.slug} to={`/crescent/products/${p.slug}`} data-testid={`product-card-${p.slug}`}
                  className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-emerald-500 hover:shadow-lg transition-all">
                  <div className={`h-36 bg-slate-100 bg-cover bg-center bg-gradient-to-br ${p.fallback_gradient || 'from-emerald-600 to-slate-800'}`} style={{ backgroundImage: `url(${p.home_card_image || p.image})` }} />
                  <div className="p-4">
                    <div className="font-bold text-slate-900 text-sm">{p.title}</div>
                  </div>
                </Link>
              )) : null}
            </div>
            <div className="mt-6 text-right">
              <Link to="/crescent/products" className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-800">
                View all products <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>

        {/* Advanced Manufacturing */}
        <section className="py-14 lg:py-20 bg-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="h-64 lg:h-80 rounded-2xl bg-slate-800 bg-cover bg-center"
              style={{ backgroundImage: "url(https://images.unsplash.com/photo-1565689157206-0fddef7589a2?w=1200)" }} />
            <div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl">Advanced Manufacturing<br/>Global Standards</h2>
              <p className="mt-4 text-slate-300 max-w-xl">
                Equipped with modern technology and a skilled team to deliver consistent quality,
                vibrant printing and reliable performance.
              </p>
              <Link to="/crescent/capabilities" className="inline-flex items-center gap-2 mt-6 bg-white text-slate-900 font-bold px-5 py-2.5 rounded-full text-sm">
                Our Capabilities <ArrowRight size={14} />
              </Link>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="capabilities-grid">
                {capabilities.map((c) => {
                  const Icon = ICONS[c.icon] || Settings;
                  return (
                    <div key={c.slug} className="flex items-center gap-2 text-sm">
                      <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                        <Icon size={14} className="text-emerald-400" />
                      </div>
                      <span className="font-semibold">{c.title}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* AI Packaging Assistant block */}
        <section className="py-14 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] gap-6 items-center" data-testid="ai-cta-block">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center text-white font-black text-3xl mx-auto lg:mx-0">AI</div>
              <div>
                <h3 className="font-heading font-extrabold text-slate-900 text-2xl">Need the Right Packaging Solution?</h3>
                <p className="text-slate-600 mt-1 text-sm">Try our AI Assistant to get quick guidance or request a custom quote.</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button data-testid="ai-chat-cta" onClick={() => window.dispatchEvent(new Event("open-crescent-chat"))} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2">
                    <MessageCircle size={14} /> Chat with AI Assistant
                  </button>
                  <Link to="/crescent/request-quote" className="bg-white hover:bg-slate-50 text-slate-900 border border-slate-300 font-bold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2">
                    Request a Quote <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
              <ul className="text-xs text-slate-600 space-y-1.5 lg:min-w-[260px]">
                {["Get packaging recommendations", "Discuss your product requirements", "Upload artwork or specifications", "Connect with our sales team"].map((p) => (
                  <li key={p} className="flex items-start gap-1.5"><CheckCircle2 size={12} className="text-emerald-500 mt-0.5 shrink-0" /> {p}</li>
                ))}
              </ul>
            </div>
            <p className="text-center text-xs text-slate-400 mt-10 uppercase tracking-widest font-bold">Trusted by Leading Brands Worldwide</p>
          </div>
        </section>
      </div>
    </CrescentLayout>
  );
}
